import React from 'react';
import './HomePage.css';

interface HomePageProps {
  onNavigateToQuote: () => void;
  onNavigateToClaims: () => void;
  onNavigateToSafeDriving: () => void;
  onNavigateToMoneySaving: () => void;
  onNavigateToChatbot: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onNavigateToQuote, onNavigateToClaims, onNavigateToSafeDriving, onNavigateToMoneySaving, onNavigateToChatbot }) => {
  const handleChatbotClick = () => {
    onNavigateToChatbot();
  };

  const handleQuoteClick = () => {
    onNavigateToQuote();
  };

  const handleClaimClick = () => {
    onNavigateToClaims();
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <main className="homepage">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Protect What Matters Most</h1>
          <p>Comprehensive insurance solutions tailored to your needs. Get peace of mind with PolicyPal.</p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={handleQuoteClick}>Get Free Quote</button>
            <button className="btn-secondary">Learn More</button>
          </div>
        </div>
        <div className="hero-image">
          <div className="placeholder-image">
            <div className="icon-vertical">🏠</div>
            <div className="icon-vertical">🚗</div>
            <div className="icon-vertical">👨‍👩‍👧‍👦</div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services">
        <h2>Our Main Services</h2>
        <div className="services-grid">
          <div className="service-card main-service">
            <div className="service-icon">💰</div>
            <h3>Quote Premium</h3>
            <p>Get instant quotes for all types of insurance coverage. Compare rates and find the best deals.</p>
            <button className="service-btn" onClick={handleQuoteClick}>Get Quote Now</button>
          </div>
          <div className="service-card main-service">
            <div className="service-icon">💬</div>
            <h3>Talk to Chatbot</h3>
            <p>24/7 AI-powered assistance for all your insurance questions and policy management needs.</p>
            <button className="service-btn" onClick={handleChatbotClick}>Chat Now</button>
          </div>
          <div className="service-card main-service">
            <div className="service-icon">📋</div>
            <h3>File Insurance Claim</h3>
            <p>Quick and easy claim filing process. Track your claim status and get updates in real-time.</p>
            <button className="service-btn" onClick={handleClaimClick}>File Claim</button>
          </div>
        </div>
      </section>

      {/* Information Sections */}
      <section className="info-sections">
        <div className="info-card">
          <h2>Safe Driving Tips</h2>
          <div className="info-content">
            <div className="info-text">
              <h3>Drive Smart, Save More</h3>
              <ul>
                <li>🚗 Maintain a clean driving record</li>
                <li>🛡️ Use defensive driving techniques</li>
                <li>📱 Avoid distractions while driving</li>
                <li>⚡ Regular vehicle maintenance</li>
                <li>🌙 Avoid driving during high-risk hours</li>
              </ul>
              <button className="info-btn" onClick={onNavigateToSafeDriving}>Learn More About Safe Driving</button>
            </div>
            <div className="info-image">
              <div className="placeholder-image">🚗🛡️</div>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h2>How to Save Money on Insurance</h2>
          <div className="info-content">
            <div className="info-text">
              <h3>Smart Ways to Reduce Your Premiums</h3>
              <ul>
                <li>🔍 Compare quotes from multiple providers</li>
                <li>📦 Bundle multiple policies together</li>
                <li>💳 Maintain good credit score</li>
                <li>🏆 Take advantage of discounts</li>
                <li>📊 Increase your deductible wisely</li>
              </ul>
              <button className="info-btn" onClick={onNavigateToMoneySaving}>Discover Money-Saving Tips</button>
            </div>
            <div className="info-image">
              <div className="placeholder-image">💰📊</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <h2>Why Choose PolicyPal?</h2>
        <div className="features-grid">
          <div className="feature">
            <div className="feature-icon">⚡</div>
            <h3>Fast Claims</h3>
            <p>Quick claim processing with our streamlined digital system</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🔒</div>
            <h3>Secure</h3>
            <p>Your data is protected with industry-leading security measures</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🏆</div>
            <h3>Award Winning</h3>
            <p>Recognized for excellence in customer service and innovation</p>
          </div>
          <div className="feature">
            <div className="feature-icon">📞</div>
            <h3>24/7 Support</h3>
            <p>Round-the-clock customer support whenever you need assistance</p>
          </div>
        </div>
      </section>

      {/* Safety Videos Section */}
      <section className="safety-videos">
        <div className="videos-container">
          <h2>Safety Education Videos</h2>
          <p>Learn essential safety tips to protect yourself and reduce insurance risks</p>
          
          <div className="videos-grid">
            <div className="video-card">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/2DROs4iyDUg"
                  title="Defensive Driving Techniques"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h3>Defensive Driving Techniques</h3>
                <p>Learn essential defensive driving skills to stay safe on the road and lower your insurance premiums.</p>
              </div>
            </div>

            <div className="video-card">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/6LfCGGvQb1o"
                  title="Home Fire Safety Tips"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h3>Home Fire Safety</h3>
                <p>Protect your home and family with these essential fire prevention and safety tips.</p>
              </div>
            </div>

            <div className="video-card">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/GaKgKLHa8OE"
                  title="Car Safety Features"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h3>Modern Car Safety Features</h3>
                <p>Understand how modern safety features work and how they can help reduce your auto insurance costs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Useful Resources Section */}
      <section className="useful-resources">
        <div className="resources-container">
          <h2>Helpful Insurance Resources</h2>
          <p>Access official government resources and trusted insurance information</p>
          
          <div className="resources-grid">
            <div className="resource-category">
              <h3>🏛️ Government Resources</h3>
              <div className="resource-links">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.nhtsa.gov/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🚗</span>
                  <div className="link-content">
                    <h4>NHTSA - Vehicle Safety</h4>
                    <p>National Highway Traffic Safety Administration official site</p>
                  </div>
                </a>
                
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.ready.gov/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🏠</span>
                  <div className="link-content">
                    <h4>Ready.gov - Emergency Preparedness</h4>
                    <p>Official U.S. government site for disaster preparedness</p>
                  </div>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.healthcare.gov/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🏥</span>
                  <div className="link-content">
                    <h4>Healthcare.gov</h4>
                    <p>Official marketplace for health insurance plans</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="resource-category">
              <h3>📊 Industry Information</h3>
              <div className="resource-links">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.iii.org/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">📈</span>
                  <div className="link-content">
                    <h4>Insurance Information Institute</h4>
                    <p>Trusted source for insurance industry facts and data</p>
                  </div>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.consumerreports.org/cro/cars/index.htm');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🔍</span>
                  <div className="link-content">
                    <h4>Consumer Reports - Auto</h4>
                    <p>Independent car reviews and safety ratings</p>
                  </div>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.redcross.org/get-help/how-to-prepare-for-emergencies');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">⛑️</span>
                  <div className="link-content">
                    <h4>Red Cross Emergency Prep</h4>
                    <p>Emergency preparedness guides and resources</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="resource-category">
              <h3>🛡️ Safety Organizations</h3>
              <div className="resource-links">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.nsc.org/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🛡️</span>
                  <div className="link-content">
                    <h4>National Safety Council</h4>
                    <p>Leading safety advocate organization</p>
                  </div>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.iihs.org/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🚙</span>
                  <div className="link-content">
                    <h4>IIHS - Highway Safety</h4>
                    <p>Insurance Institute for Highway Safety research</p>
                  </div>
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExternalLink('https://www.usfa.fema.gov/');
                  }}
                  className="resource-link"
                >
                  <span className="link-icon">🔥</span>
                  <div className="link-content">
                    <h4>U.S. Fire Administration</h4>
                    <p>Fire safety education and prevention resources</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="testimonials-container">
          <h2>What Our Customers Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"PolicyPal made filing my auto claim so easy! The process was clear and I got updates every step of the way. Excellent service!"</p>
                <div className="testimonial-author">
                  <strong>Sarah Johnson</strong>
                  <span>Auto Insurance Customer</span>
                </div>
              </div>
            </div>

            <div className="testimonial">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"The AI chatbot answered all my questions instantly. It's like having an insurance expert available 24/7. Very impressed!"</p>
                <div className="testimonial-author">
                  <strong>Mike Chen</strong>
                  <span>Home Insurance Customer</span>
                </div>
              </div>
            </div>

            <div className="testimonial">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Got my quote in minutes and saved $400 a year by bundling my policies. The website is so user-friendly!"</p>
                <div className="testimonial-author">
                  <strong>Emily Rodriguez</strong>
                  <span>Multi-Policy Customer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter">
        <div className="newsletter-container">
          <div className="newsletter-content">
            <h2>Stay Protected with Our Safety Tips</h2>
            <p>Get monthly safety tips, insurance updates, and exclusive offers delivered to your inbox.</p>
            
            <form className="newsletter-form" onSubmit={(e) => {
              e.preventDefault();
              alert('Thank you for subscribing! You will receive safety tips and updates.');
            }}>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  required
                  className="email-input"
                />
                <button type="submit" className="subscribe-btn">
                  Subscribe
                </button>
              </div>
              <div className="newsletter-benefits">
                <div className="benefit">
                  <span className="benefit-icon">📧</span>
                  <span>Monthly Safety Tips</span>
                </div>
                <div className="benefit">
                  <span className="benefit-icon">💰</span>
                  <span>Exclusive Discounts</span>
                </div>
                <div className="benefit">
                  <span className="benefit-icon">🚨</span>
                  <span>Insurance Updates</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Insurance Tips Section */}
      <section className="insurance-tips">
        <div className="tips-container">
          <h2>Monthly Insurance Tips</h2>
          <div className="tips-grid">
            <div className="tip-card">
              <div className="tip-icon">🏠</div>
              <h3>Home Inventory</h3>
              <p>Create a detailed inventory of your belongings with photos and receipts. Store it securely online for easy access during claims.</p>
              <a href="#" className="tip-link">Learn More →</a>
            </div>

            <div className="tip-card">
              <div className="tip-icon">🚗</div>
              <h3>Vehicle Maintenance</h3>
              <p>Regular maintenance not only keeps your car safe but can also qualify you for safe driver discounts with many insurers.</p>
              <a href="#" className="tip-link">Learn More →</a>
            </div>

            <div className="tip-card">
              <div className="tip-icon">💡</div>
              <h3>Security Systems</h3>
              <p>Installing security systems, smoke detectors, and anti-theft devices can significantly reduce your insurance premiums.</p>
              <a href="#" className="tip-link">Learn More →</a>
            </div>

            <div className="tip-card">
              <div className="tip-icon">📄</div>
              <h3>Policy Reviews</h3>
              <p>Review your insurance policies annually to ensure coverage meets your current needs and you're getting the best rates.</p>
              <a href="#" className="tip-link">Learn More →</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Get Protected?</h2>
          <p>Join thousands of satisfied customers who trust PolicyPal with their insurance needs.</p>
          <button className="btn-primary large" onClick={handleQuoteClick}>Start Your Quote Today</button>
        </div>
      </section>
    </main>
  );
};

export default HomePage;